// src/components/UserTable.js
import React, { useState, useEffect } from 'react';
// Importez collection et where, en plus de query et onSnapshot
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { User, Mail, Briefcase, Loader2, Users as UsersIcon } from 'lucide-react';

// Ajoutez schoolId aux props et corrigez la casse de userRole
const UserTable = ({ db, setMessage, setError, userRole, schoolId }) => {
  const [users, setUsers] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);

  useEffect(() => {
    if (!db) {
      setError('Base de données Firestore non disponible.');
      setTableLoading(false);
      return;
    }

    setTableLoading(true);
    setError('');
    setMessage('');

    let usersQueryRef;

    // --- LOGIQUE DE CONSTRUCTION DE LA REQUÊTE SELON LE RÔLE ET LE schoolId ---
    if (userRole === 'superAdmin') {
      // Si le SuperAdmin a sélectionné une école spécifique, filtre par cette école
      if (schoolId) {
        usersQueryRef = query(collection(db, 'users'), where('schoolId', '==', schoolId));
      } else {
        // Si le SuperAdmin n'a pas sélectionné d'école, ne charge rien pour l'instant
        setUsers([]);
        setTableLoading(false);
        setMessage("Super Admin : Veuillez sélectionner une école pour afficher ses utilisateurs.");
        return;
      }
    } else if (userRole === 'admin' || userRole === 'secretaireGeneral') {
      // Admin ou Secrétaire Général ne peut voir que les utilisateurs de LEUR école.
      // Le schoolId est fourni par les props depuis AdminDashboard.
      if (schoolId) {
        usersQueryRef = query(collection(db, 'users'), where('schoolId', '==', schoolId));
      } else {
        // Cela ne devrait normalement pas arriver si AdminDashboard.js fonctionne correctement,
        // mais c'est une sécurité.
        setError("Impossible de charger les utilisateurs : l'ID de l'école est manquant pour votre rôle.");
        setTableLoading(false);
        return;
      }
    } else {
      // Pour les autres rôles, afficher un message d'erreur et ne rien charger
      setError('Vous n\'avez pas les permissions nécessaires pour voir cette liste d\'utilisateurs.');
      setTableLoading(false);
      return;
    }
    // --- FIN DE LA LOGIQUE DE CONSTRUCTION DE LA REQUÊTE ---

    const unsubscribe = onSnapshot(usersQueryRef, (snapshot) => {
      const usersList = [];
      snapshot.forEach((doc) => {
        usersList.push({
          id: doc.id, // Pour la collection /users/{userId}, le doc.id est l'UID de l'utilisateur
          ...doc.data(),
          email: doc.data().email || 'N/A',
          role: doc.data().role || 'Non défini',
          schoolId: doc.data().schoolId || 'Non défini',
        });
      });
      setUsers(usersList);
      setTableLoading(false);
      setMessage('Liste des utilisateurs chargée avec succès.');
    }, (error) => {
      console.error("Erreur lors du chargement des profils utilisateurs:", error);
      setError(`Erreur lors du chargement des utilisateurs: ${error.message}`);
      setTableLoading(false);
    });

    return () => unsubscribe();
    // Ajoutez schoolId aux dépendances du useEffect
  }, [db, setMessage, setError, userRole, schoolId]); // IMPORTANT : userRole et schoolId doivent être des dépendances

  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
        <UsersIcon size={24} className="mr-2" /> Liste des Utilisateurs
        {userRole !== 'superAdmin' && schoolId && <span className="ml-2 text-sm text-gray-500">(Votre École)</span>}
        {userRole === 'superAdmin' && schoolId && <span className="ml-2 text-sm text-gray-500">(École sélectionnée)</span>}
      </h2>

      {tableLoading ? (
        <div className="flex items-center justify-center p-6 text-blue-600">
          <Loader2 className="animate-spin mr-2" size={20} /> Chargement des utilisateurs...
        </div>
      ) : users.length === 0 ? (
        <p className="text-center text-gray-500 py-6">
          {userRole === 'superAdmin' && !schoolId
            ? "Veuillez sélectionner une école pour afficher les utilisateurs."
            : "Aucun utilisateur trouvé ou vous n'avez pas les permissions nécessaires."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <User size={16} className="inline mr-1" /> UID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <Mail size={16} className="inline mr-1" /> Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <Briefcase size={16} className="inline mr-1" /> Rôle
                </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID École
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{user.role}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{user.schoolId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserTable;
